import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../components/brand/Navbar';
import Footer from '../components/brand/Footer';
import { blogPosts } from '../data/blogPosts';

const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

// Parse {LINK:text|/path} markers in paragraph text and return an array of
// strings / <Link> elements suitable for rendering inside a <p>.
const renderText = (text) => {
  const linkRegex = /\{LINK:([^|]+)\|([^}]+)\}/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ kind: 'text', value: text.slice(lastIndex, match.index) });
    }
    parts.push({ kind: 'link', text: match[1], href: match[2] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ kind: 'text', value: text.slice(lastIndex) });
  }

  if (parts.length === 0) return text;
  if (parts.length === 1 && parts[0].kind === 'text') return text;

  return parts.map((part, i) => {
    if (part.kind === 'text') {
      return <React.Fragment key={i}>{part.value}</React.Fragment>;
    }
    return (
      <Link
        key={i}
        to={part.href}
        className="font-medium hover:underline"
        style={{ color: '#80E5CB' }}
      >
        {part.text}
      </Link>
    );
  });
};

const ContentSection = ({ section }) => {
  if (section.type === 'h2') {
    return (
      <h2
        className="font-bold mt-10 mb-4"
        style={{
          color: '#00001E',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
          letterSpacing: '-0.02em',
          lineHeight: 1.25,
        }}
      >
        {section.text}
      </h2>
    );
  }

  if (section.type === 'p') {
    return (
      <p
        className="mb-5 leading-relaxed"
        style={{
          color: '#2B3879',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          fontSize: '1rem',
          lineHeight: '1.75',
        }}
      >
        {renderText(section.text)}
      </p>
    );
  }

  if (section.type === 'cta') {
    return (
      <div
        style={{
          background: 'rgba(128,229,203,0.10)',
          border: '1.5px solid #80E5CB',
          borderRadius: '14px',
          padding: '1.75rem 2rem',
          margin: '2.5rem 0',
        }}
      >
        <p
          style={{
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            color: '#00001E',
            fontWeight: 700,
            fontSize: '1.08rem',
            marginBottom: '0.5rem',
            lineHeight: 1.4,
          }}
        >
          {section.heading}
        </p>
        {section.body && (
          <p
            style={{
              color: '#2B3879',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontSize: '0.95rem',
              lineHeight: 1.65,
              marginBottom: '1.25rem',
            }}
          >
            {section.body}
          </p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap' }}>
          <Link
            to={section.buttonHref || '/booking'}
            style={{
              display: 'inline-block',
              background: '#80E5CB',
              color: '#00001E',
              borderRadius: '8px',
              padding: '11px 24px',
              fontWeight: 700,
              fontSize: '0.88rem',
              textDecoration: 'none',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              boxShadow: '0 2px 12px rgba(128,229,203,0.25)',
            }}
          >
            {section.buttonText}
          </Link>
          {section.phone && (
            <a
              href={`tel:+61${section.phone.replace(/\s/g, '').replace(/^0/, '')}`}
              style={{
                color: '#2B3779',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                fontSize: '0.9rem',
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Or call us: {section.phone}
            </a>
          )}
        </div>
      </div>
    );
  }

  if (section.type === 'sources') {
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: '0.5rem 0 0' }}>
        {section.items.map((item, i) => (
          <li
            key={i}
            style={{
              color: '#2B3779',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
              fontSize: '0.85rem',
              lineHeight: 1.65,
              opacity: 0.72,
              paddingLeft: '1.2em',
              textIndent: '-1.2em',
              marginBottom: '0.3rem',
            }}
          >
            {'– ' + item}
          </li>
        ))}
      </ul>
    );
  }

  return null;
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  // Related posts: all posts except current, take first 2
  const relatedPosts = blogPosts
    .filter((p) => p.slug !== slug)
    .slice(0, 2);

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'view_blog_post', {
        event_category: 'engagement',
        event_label: post?.title || slug,
      });
    }
  }, [slug, post]);

  if (!post) {
    return (
      <>
        <div className="min-h-screen bg-[#F7F9FB]">
          <Navbar onBookClick={() => { window.location.href = '/booking'; }} />
          <main className="pt-40 pb-32">
            <div className="container max-w-3xl mx-auto px-6 text-center">
              <h1
                className="font-bold mb-4 text-[#00001E]"
                style={{
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontSize: '2rem',
                }}
              >
                Post not found
              </h1>
              <p className="text-[#2B3879] mb-8">
                We couldn't find that blog post.
              </p>
              <Link
                to="/blog"
                style={{
                  display: 'inline-block',
                  background: '#80E5CB',
                  color: '#00001E',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  textDecoration: 'none',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                ← Back to Blog
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={`https://nurahealth.com.au/blog/${post.slug}`} />
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Nura Health" />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={`https://nurahealth.com.au/blog/${post.slug}`} />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        {/* BlogPosting schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.metaDescription,
            datePublished: post.date,
            author: {
              '@type': 'Organization',
              name: 'Nura Health',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Nura Health',
              url: 'https://nurahealth.com.au',
            },
            mainEntityOfPage: `https://nurahealth.com.au/blog/${post.slug}`,
          })}
        </script>
        {/* FAQPage schema (present only when post defines faqEntries) */}
        {post.faqEntries && post.faqEntries.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: post.faqEntries.map((f) => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            })}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen" style={{ background: '#F7F9FB' }}>
        <Navbar onBookClick={() => { window.location.href = '/booking'; }} />

        <main>
          {/* Post header — dark banner */}
          <header
            className="pt-36 pb-14 relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #00001E 0%, #0a0b2e 65%, #101a3e 100%)' }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(ellipse at 15% 60%, rgba(128,229,203,0.08) 0%, transparent 50%)',
            }} />
            <div className="container max-w-3xl mx-auto px-6 relative z-10">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm mb-7 transition-colors"
                style={{
                  color: 'rgba(255,255,255,0.65)',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  textDecoration: 'none',
                  fontWeight: 500,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 8H3M3 8L7 4M3 8L7 12" stroke="rgba(255,255,255,0.65)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back to Blog
              </Link>

              <div className="flex items-center gap-3 mb-5 flex-wrap">
                <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.60)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                  {formatDate(post.date)}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.30)' }}>·</span>
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                  {post.readTime}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.30)' }}>·</span>
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
                  By {post.author}
                </span>
              </div>

              <h1
                className="font-extrabold"
                style={{
                  color: '#FFFFFF',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontSize: 'clamp(1.75rem, 4vw, 2.6rem)',
                  letterSpacing: '-0.03em',
                  lineHeight: 1.15,
                }}
              >
                {post.title}
              </h1>
            </div>
          </header>

          {/* Hero image — shown when post.heroImage is set; falls back gracefully */}
          {post.heroImage && (
            <div className="container max-w-3xl mx-auto px-6" style={{ paddingTop: '2.5rem' }}>
              <div
                style={{
                  position: 'relative',
                  aspectRatio: '16 / 9',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, #2B3879 0%, #1a245e 100%)',
                  boxShadow: '0 8px 40px rgba(43,56,121,0.18)',
                }}
              >
                <img
                  src={post.heroImage}
                  alt={post.heroAlt}
                  width={1200}
                  height={675}
                  loading="eager"
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
            </div>
          )}

          {/* Post content */}
          <article className="py-14">
            <div className="container max-w-3xl mx-auto px-6">
              {post.content.map((section, i) => (
                <ContentSection key={i} section={section} />
              ))}
            </div>
          </article>

          {/* Divider */}
          <div
            className="container max-w-3xl mx-auto px-6"
            style={{ borderTop: '1px solid rgba(43,56,121,0.10)' }}
          />

          {/* CTA */}
          <section className="py-14 bg-white">
            <div className="container max-w-3xl mx-auto px-6">
              <div
                className="rounded-2xl p-8 md:p-10 text-center"
                style={{
                  background: '#F7F9FB',
                  border: '1px solid rgba(43,56,121,0.12)',
                  boxShadow: '0 4px 24px rgba(43,56,121,0.07)',
                }}
              >
                <span
                  className="pill-label"
                  style={{ display: 'inline-block', marginBottom: '0.75rem' }}
                >
                  Ready to Book?
                </span>
                <h3
                  className="font-bold mb-3 text-[#00001E]"
                  style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    fontSize: 'clamp(1.3rem, 3vw, 1.7rem)',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Book your mobile blood collection today
                </h3>
                <p
                  className="mb-7 leading-relaxed"
                  style={{
                    color: '#2B3879',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    fontSize: '0.97rem',
                  }}
                >
                  A qualified Nura Health phlebotomist comes to you — at home, at work, or at
                  your aged care facility. Call{' '}
                  <a
                    href="tel:+61420424290"
                    style={{ color: '#2B3879', fontWeight: 600 }}
                    onClick={() => {
                      if (window.gtag) {
                        window.gtag('event', 'click_phone', {
                          event_category: 'engagement',
                          event_label: 'Phone number clicked',
                        });
                      }
                    }}
                  >
                    0420 424 290
                  </a>{' '}
                  or book online below.
                </p>
                <Link
                  to="/booking"
                  style={{
                    display: 'inline-block',
                    background: '#80E5CB',
                    color: '#00001E',
                    borderRadius: '8px',
                    padding: '13px 30px',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    textDecoration: 'none',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    boxShadow: '0 4px 16px rgba(128,229,203,0.2)',
                  }}
                >
                  Book a Collection
                </Link>
              </div>
            </div>
          </section>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <section className="pb-20 bg-[#F7F9FB]">
              <div className="container max-w-3xl mx-auto px-6">
                <h2
                  className="font-bold mb-8 text-[#00001E]"
                  style={{
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    fontSize: '1.3rem',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Related Posts
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {relatedPosts.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/blog/${related.slug}`}
                      className="group block"
                      style={{ textDecoration: 'none' }}
                    >
                      <div
                        className="rounded-xl p-6 h-full flex flex-col transition-all duration-300 bg-white"
                        style={{
                          border: '1px solid rgba(43,56,121,0.10)',
                          boxShadow: '0 2px 12px rgba(43,56,121,0.05)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.border = '1px solid rgba(43,56,121,0.25)';
                          e.currentTarget.style.boxShadow = '0 4px 20px rgba(43,56,121,0.08)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.border = '1px solid rgba(43,56,121,0.10)';
                          e.currentTarget.style.boxShadow = '0 2px 12px rgba(43,56,121,0.05)';
                        }}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <span
                            className="text-xs"
                            style={{
                              color: '#2B3879',
                              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                            }}
                          >
                            {formatDate(related.date)}
                          </span>
                          <span style={{ color: '#2B3879', opacity: 0.4, fontSize: '0.75rem' }}>·</span>
                          <span
                            className="text-xs"
                            style={{
                              color: '#2B3879',
                              opacity: 0.7,
                              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                            }}
                          >
                            {related.readTime}
                          </span>
                        </div>
                        <h3
                          className="font-semibold mb-2 group-hover:text-[#2B3879] transition-colors duration-200"
                          style={{
                            color: '#00001E',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                            fontSize: '0.97rem',
                            lineHeight: 1.4,
                          }}
                        >
                          {related.title}
                        </h3>
                        <p
                          className="text-xs leading-relaxed mt-auto pt-2"
                          style={{
                            color: '#2B3879',
                            opacity: 0.75,
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                          }}
                        >
                          {related.excerpt}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default BlogPost;
